/**
 * Auth Modal Script
 * Handles authentication modal functionality: open/close, form submission, screen switching.
 */

(function () {
    'use strict';

    // Check if required data is available
    if (typeof window.saborlistaAuthModal === 'undefined') {
        return;
    }

    const config = window.saborlistaAuthModal;
    
    let modal = null;
    let overlay = null;
    let dialog = null;
    let closeButtons = null;
    let loginForm = null;
    let lostPasswordForm = null;
    let messagesContainer = null;
    let progressBar = null;
    let titleElement = null;
    let switchLinks = null;

    let isSubmitting = false;
    let progressAnimation = null;

    /**
     * Fetches a fresh wp_rest nonce from an uncacheable REST endpoint.
     * Cached HTML may contain a stale nonce from wp_localize_script.
     */
    function fetchRestNonce() {
        return fetch(config.restUrl + '/nonce', {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { 'Accept': 'application/json' }
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('nonce_fetch_failed');
            }
            return response.json();
        })
        .then(data => {
            if (!data || !data.nonce) {
                throw new Error('nonce_missing');
            }
            return data.nonce;
        });
    }

    /**
     * Shows a message in the messages container.
     */
    function showMessage(message, type) {
        if (!messagesContainer) return;
        messagesContainer.innerHTML = '<div class="alert-' + type + '">' + escapeHtml(message) + '</div>';
        messagesContainer.style.display = 'block';
    }

    /**
     * Hides the messages container.
     */
    function hideMessage() {
        if (messagesContainer) {
            messagesContainer.innerHTML = '';
            messagesContainer.style.display = 'none';
        }
    }

    /**
     * Escapes HTML in a string.
     */
    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Resets the modal state.
     */
    function resetModal() {
        if (!modal) return;
        hideMessage();
        resetProgress();
        if (loginForm) loginForm.reset();
        if (lostPasswordForm) lostPasswordForm.reset();
        
        const errorFields = modal.querySelectorAll('.field.error, .field.is-invalid');
        errorFields.forEach(field => field.classList.remove('error', 'is-invalid'));
        
        switchScreen('login');
    }

    function openModal() {
        if (!modal) return;
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        
        const usernameField = document.getElementById('auth-modal-username');
        if (usernameField) {
            setTimeout(() => usernameField.focus(), 100);
        }
    }

    function closeModal() {
        if (!modal) return;
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        resetModal();
    }

    function switchScreen(screen) {
        hideMessage();
        resetProgress();

        if (screen === 'login') {
            if (loginForm) loginForm.classList.remove('auth-modal__form--hidden');
            if (lostPasswordForm) lostPasswordForm.classList.add('auth-modal__form--hidden');
            if (titleElement) {
                titleElement.textContent = (config.i18n && config.i18n.loginTitle) || 'Вход';
            }
        } else if (screen === 'lost-password') {
            if (loginForm) loginForm.classList.add('auth-modal__form--hidden');
            if (lostPasswordForm) lostPasswordForm.classList.remove('auth-modal__form--hidden');
            if (titleElement) {
                titleElement.textContent = (config.i18n && config.i18n.lostPasswordTitle) || 'Восстановление пароля';
            }
        }
    }

    function startProgress() {
        if (!progressBar) return;
        progressBar.style.width = '0%';
        progressBar.style.display = 'block';

        let progress = 0;
        const targetProgress = 90;
        const duration = 2000;
        const startTime = Date.now();

        function animate() {
            const elapsed = Date.now() - startTime;
            progress = Math.min((elapsed / duration) * targetProgress, targetProgress);
            progressBar.style.width = progress + '%';
            if (progress < targetProgress) {
                progressAnimation = requestAnimationFrame(animate);
            }
        }
        progressAnimation = requestAnimationFrame(animate);
    }

    function completeProgress() {
        if (progressAnimation) cancelAnimationFrame(progressAnimation);
        if (!progressBar) return;
        progressBar.style.width = '100%';
        setTimeout(resetProgress, 300);
    }

    function resetProgress() {
        if (progressAnimation) cancelAnimationFrame(progressAnimation);
        if (progressBar) {
            progressBar.style.width = '0%';
            progressBar.style.display = 'none';
        }
    }

    /**
     * Loads a fresh document after AJAX login so the server-rendered service
     * menu sees the auth cookie. Assigning the current URL is not a reload
     * when a hash is present or history.pushState already changed the address.
     */
    function continueAfterLogin(redirectTo) {
        let targetUrl;

        try {
            targetUrl = new URL(redirectTo || window.location.href, window.location.origin);
        } catch (err) {
            window.location.reload();
            return;
        }

        const currentUrl = new URL(window.location.href);
        const sameDocument = targetUrl.origin === currentUrl.origin
            && targetUrl.pathname === currentUrl.pathname
            && targetUrl.search === currentUrl.search;

        if (sameDocument || targetUrl.origin !== currentUrl.origin) {
            window.location.reload();
            return;
        }

        window.location.assign(targetUrl.href);
    }

    /**
     * Asks Chromium password managers to save credentials after AJAX login.
     * Does not block redirect: store() may wait for the user indefinitely.
     */
    function storeLoginCredential(username, password) {
        if (!username || !password) {
            return;
        }
        if (typeof window.PasswordCredential !== 'function') {
            return;
        }
        if (!navigator.credentials || typeof navigator.credentials.store !== 'function') {
            return;
        }
        try {
            navigator.credentials.store(new PasswordCredential({
                id: username,
                password: password
            })).catch(function () {
                // Dismissed or unsupported; login still succeeds.
            });
        } catch (err) {
            // Ignore.
        }
    }

    function handleLoginSubmit(e) {
        e.preventDefault();
        if (isSubmitting) return;

        const usernameField = document.getElementById('auth-modal-username');
        const passwordField = document.getElementById('auth-modal-password');
        const rememberField = document.getElementById('auth-modal-remember');

        const username = usernameField ? usernameField.value.trim() : '';
        const password = passwordField ? passwordField.value : '';
        
        if (!username || !password) {
            showMessage((config.i18n && config.i18n.fillFields) || 'Please fill in all fields.', 'error');
            if (!username && usernameField) usernameField.classList.add('error', 'is-invalid');
            if (!password && passwordField) passwordField.classList.add('error', 'is-invalid');
            return;
        }

        isSubmitting = true;
        startProgress();

        const submitButton = loginForm.querySelector('button[type="submit"]');
        if (submitButton) submitButton.disabled = true;

        const formData = new FormData(loginForm);
        formData.append('remember', rememberField && rememberField.checked ? '1' : '0');

        fetchRestNonce()
            .then(nonce => fetch(config.restUrl + '/login', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-WP-Nonce': nonce },
                body: formData
            }))
            .then(response => response.json())
            .then(data => {
                completeProgress();
                if (data.success) {
                    storeLoginCredential(username, password);
                    if (window.SaborlistaNotifications) {
                        window.SaborlistaNotifications.success((config.i18n && config.i18n.loginSuccess) || 'Success!');
                    }
                    setTimeout(() => {
                        continueAfterLogin(data.redirect_to);
                    }, 1000);
                } else {
                    showMessage(data.message || ((config.i18n && config.i18n.loginUnknownError) || 'Error'), 'error');
                    if (submitButton) submitButton.disabled = false;
                    isSubmitting = false;
                }
            })
            .catch(() => {
                completeProgress();
                showMessage((config.i18n && config.i18n.loginNetworkError) || 'Network error', 'error');
                if (submitButton) submitButton.disabled = false;
                isSubmitting = false;
            });
    }

    function handleLostPasswordSubmit(e) {
        e.preventDefault();
        if (isSubmitting) return;

        const userLoginField = document.getElementById('auth-modal-lost-username');
        const userLogin = userLoginField ? userLoginField.value.trim() : '';

        if (!userLogin) {
            showMessage((config.i18n && config.i18n.enterEmail) || 'Please enter email address or username.', 'error');
            if (userLoginField) userLoginField.classList.add('error', 'is-invalid');
            return;
        }

        isSubmitting = true;
        startProgress();

        const submitButton = lostPasswordForm.querySelector('button[type="submit"]');
        if (submitButton) submitButton.disabled = true;

        const formData = new FormData(lostPasswordForm);

        fetchRestNonce()
            .then(nonce => fetch(config.restUrl + '/lost-password', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'X-WP-Nonce': nonce },
                body: formData
            }))
            .then(response => response.json())
            .then(data => {
                completeProgress();
                if (data.success) {
                    showMessage(
                        data.message || ((config.i18n && config.i18n.passwordResetSent) || 'If an account exists, password reset instructions have been sent to your email.'),
                        'success'
                    );
                    if (submitButton) submitButton.disabled = false;
                    isSubmitting = false;
                } else {
                    showMessage(data.message || ((config.i18n && config.i18n.loginUnknownError) || 'Error'), 'error');
                    if (submitButton) submitButton.disabled = false;
                    isSubmitting = false;
                }
            })
            .catch(() => {
                completeProgress();
                showMessage((config.i18n && config.i18n.passwordResetNetworkError) || 'Network error', 'error');
                if (submitButton) submitButton.disabled = false;
                isSubmitting = false;
            });
    }

    function init() {
        modal = document.getElementById('auth-modal');
        if (!modal) return;

        overlay = modal.querySelector('.auth-modal__overlay');
        dialog = modal.querySelector('.auth-modal__dialog');
        closeButtons = modal.querySelectorAll('[data-auth-modal-close]');
        loginForm = document.getElementById('auth-modal-login-form');
        lostPasswordForm = document.getElementById('auth-modal-lost-password-form');
        messagesContainer = document.getElementById('auth-modal-messages');
        progressBar = document.getElementById('auth-modal-progress-bar');
        titleElement = document.getElementById('auth-modal-title');
        switchLinks = modal.querySelectorAll('[data-auth-modal-switch]');
        
        closeButtons.forEach(btn => btn.addEventListener('click', e => { e.preventDefault(); closeModal(); }));
        
        document.addEventListener('keydown', e => {
            if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
        });

        switchLinks.forEach(link => link.addEventListener('click', e => {
            e.preventDefault();
            switchScreen(link.getAttribute('data-auth-modal-switch'));
        }));

        if (loginForm) loginForm.addEventListener('submit', handleLoginSubmit);
        if (lostPasswordForm) lostPasswordForm.addEventListener('submit', handleLostPasswordSubmit);

        // Delegation for triggers
        document.body.addEventListener('click', e => {
            if (e.target.closest('.js-auth-modal-open')) {
                e.preventDefault();
                openModal();
            }
        });

        switchScreen('login');
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
